// src/utils/trades.js — DropPin trade catalogue.
// ----------------------------------------------------------------------
// v0.9.0 — frontend-only catalogue of defect "trades" + their colours.
// Each defect can carry a `trade` key (Defects.Trade NVARCHAR(40)); the
// PlanScreen pin renderer + RegisterScreen thumbnail border + PDF
// template all read tradeColor(key) to colour-code by trade.
//
// Why hard-code here rather than a DB lookup table:
//   • the list is small + stable; a Tenants-scoped trade catalogue is a
//     v1.x feature (every customer adds 1-2 niche trades).
//   • the colour palette is tuned for accessibility on light & dark plan
//     backgrounds — leaving it in code keeps the design intent next to
//     where it's used.
//
// Multi-tenant note: once tenants can customise their trade list, this
// becomes the FALLBACK constant + the API ships a TenantTrades override.

export const TRADES = [
  { key: 'general',    label: 'General',      color: '#6b7280' }, // default
  { key: 'plumbing',   label: 'Plumbing',     color: '#3b82f6' },
  { key: 'electrical', label: 'Electrical',   color: '#eab308' },
  { key: 'tiling',     label: 'Tiling',       color: '#06b6d4' },
  { key: 'carpentry',  label: 'Carpentry',    color: '#a16207' },
  { key: 'painting',   label: 'Painting',     color: '#ec4899' },
  { key: 'hvac',       label: 'HVAC',         color: '#10b981' },
  { key: 'roofing',    label: 'Roofing',      color: '#8b5cf6' },
  { key: 'flooring',   label: 'Flooring',     color: '#f97316' },
  { key: 'glazing',    label: 'Glazing',      color: '#14b8a6' },
  { key: 'other',      label: 'Other',        color: '#64748b' },
];

// ── THE TENANT'S OWN LIST, when there is one (2026-08-14) ─────────────────
// Exactly what this file's header predicted: TRADES becomes the FALLBACK and
// the API ships an override (Settings → Mark types → /api/mark-catalogue).
//
// The override is held HERE, as module state, so tradeColor/tradeLabel stay
// SYNCHRONOUS. That matters: they are called from nine places including PDF
// generation and the public report, where an async lookup would mean threading
// a promise through rendering code that has no business knowing where a colour
// came from. One load at startup, every call site unchanged.
//
// Falls back to TRADES on anything unexpected, so a tenant who has never opened
// Settings — or an offline device, or a failed request — behaves exactly as the
// app always has.
let _catalogue = TRADES;
const _listeners = new Set();

/** The live list: the tenant's types when loaded, else the built-in trades. */
export function getTrades() {
  return _catalogue;
}

/**
 * Install the tenant's catalogue. Ignores an empty or malformed list rather
 * than emptying the picker — no trades at all is worse than the defaults.
 */
export function setTradeCatalogue(list) {
  const clean = (Array.isArray(list) ? list : [])
    .filter((t) => t && typeof t.key === 'string' && t.key
      && typeof t.color === 'string' && /^#[0-9a-f]{6}$/i.test(t.color))
    .map((t) => ({ key: t.key, label: t.label || t.key, color: t.color, active: t.active !== false }));
  if (!clean.length) return false;
  _catalogue = clean;
  _listeners.forEach((fn) => { try { fn(_catalogue); } catch (_) { /* a bad listener is not fatal */ } });
  return true;
}

/** Subscribe to catalogue changes — React components need to re-render. */
export function subscribeTrades(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// A retired type keeps its colour and label so OLD marks still read correctly —
// it just stops being offered. That is why lookups search the whole catalogue
// while pickers filter on `active`.
export function tradeColor(key) {
  const list = _catalogue;
  return (list.find(t => t.key === key) || list[0] || TRADES[0]).color;
}

export function tradeLabel(key) {
  const list = _catalogue;
  return (list.find(t => t.key === key) || list[0] || TRADES[0]).label;
}

// Returns the {r,g,b} triple for jsPDF setFillColor calls. Falls back
// to the 'general' grey if the key is unknown.
export function tradeRgb(key) {
  const hex = tradeColor(key);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}
